import React, { useState, useEffect, useCallback } from 'react';
import adminService from '../services/adminService';
import { toast } from 'react-toastify';

const AdminReportsTab = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching reports from adminService');
      
      const response = await adminService.getReports();
      console.log('Reports response:', response);
      
      // Handle different API response formats
      let reportsData = [];
      
      if (response && response.reports && Array.isArray(response.reports)) {
        reportsData = response.reports;
      } else if (response && Array.isArray(response)) {
        reportsData = response;
      } else if (response && typeof response === 'object') {
        // Try to find any array in the response
        const possibleArrays = Object.values(response).filter(Array.isArray);
        if (possibleArrays.length > 0) {
          reportsData = possibleArrays[0];
        }
      }
      
      console.log(`Found ${reportsData.length} reports to display`);
      setReports(reportsData);
    } catch (error) {
      console.error('Error loading reports:', error);
      setError('Failed to load reports');
      toast.error('Failed to load reports');
      
      // Use fallback data in case of API failure
      setReports([{
        id: 'demo-1',
        title: 'Demo Report',
        description: 'This is a fallback demo report',
        status: 'pending',
        createdAt: new Date().toISOString(),
        reporterName: 'Demo User'
      }]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleUpdateStatus = useCallback(async (id, status) => {
    try {
      await adminService.updateReportStatus(id, { status });
      toast.success('Report status updated');
      loadReports();
    } catch (error) {
      toast.error('Failed to update report status');
    }
  }, [loadReports]);

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'investigating':
        return 'bg-blue-100 text-blue-800';
      case 'resolved':
        return 'bg-green-100 text-green-800';
      case 'dismissed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
        <p>{error}</p>
        <button 
          onClick={loadReports}
          className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold mb-4">Reports</h2>
      {!Array.isArray(reports) ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded">
          <p className="text-red-600">Invalid data format received. Please refresh the page.</p>
        </div>
      ) : reports.length === 0 ? (
        <div className="text-center p-8">
          <p className="text-gray-500">No reports to display</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {Array.isArray(reports) && reports.map((report) => (
            <div key={report.id} className="bg-white p-4 rounded-lg shadow">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center space-x-2">
                    <h3 className="font-medium">Report #{report.id}</h3>
                    <span
                      className={`px-2 py-1 rounded text-sm ${getStatusBadgeColor(
                        report.status
                      )}`}
                    >
                      {report.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    Reported by: {report.reporterName} on {report.createdAt}
                  </p>
                  <p className="mt-2">{report.description}</p>
                  {report.listingId && (
                    <a
                      href={`/listings/${report.listingId}`}
                      className="text-blue-600 hover:underline mt-2 block"
                    >
                      View Related Listing
                    </a>
                  )}
                </div>
                <div className="space-x-2">
                  {report.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleUpdateStatus(report.id, 'investigating')}
                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Investigate
                      </button>
                      <button
                        onClick={() => handleUpdateStatus(report.id, 'dismissed')}
                        className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                      >
                        Dismiss
                      </button>
                    </>
                  )}
                  {report.status === 'investigating' && (
                    <button
                      onClick={() => handleUpdateStatus(report.id, 'resolved')}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                    >
                      Mark Resolved
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminReportsTab;
